"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { CircleCheck, HeartHandshake, Scale, Users } from "lucide-react";
const principles = [{
  title: "Fairness",
  description: "People should understand what is expected, how decisions are made, and how their work is being assessed.",
  icon: Scale
}, {
  title: "Respect",
  description: "We value clear communication, professional conduct, and a workplace where people are treated with care.",
  icon: HeartHandshake
}, {
  title: "Contribution",
  description: "Growth is connected to the quality of your work, your reliability, your judgment, and the support you provide to clients and teammates.",
  icon: CircleCheck
}, {
  title: "Belonging",
  description: "Different experiences and perspectives strengthen our work, especially when serving communities and organizations with their own priorities and ways of working.",
  icon: Users
}];
export default function IntroHeadlineRepairService() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="253408" id="a-respectful-merit-based-environment" className="bg-slate-50 text-foreground py-16 md:py-24 border-b border-border/40">
      <div ref={ref} className={cn("max-w-7xl mx-auto px-6 lg:px-8 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="h-1 w-12 bg-secondary-500 mx-auto mb-6 rounded-full" />

          <p className="text-secondary-700 font-semibold tracking-[0.18em] text-xs uppercase mb-4 font-default">
            How We Work Together
          </p>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-600 mb-6 font-default leading-tight tracking-tight">
            A respectful, merit-based environment.
          </h2>

          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default">
            RiverBend is building a workplace where people are treated fairly,
            expectations are clear, and growth is connected to contribution,
            judgment, reliability, and care for the work.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 mt-12 md:mt-16">
          {principles.map((item, index) => {
          const Icon = item.icon;
          return <div key={index} data-index={index} className="bg-white border border-border/70 rounded-2xl p-6 shadow-sm h-full">
                <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-secondary-700" />
                </div>

                <h3 className="font-default text-xl font-semibold text-primary-600 mb-3 leading-snug">
                  {item.title}
                </h3>

                <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>;
        })}
        </div>

        <div className="max-w-4xl mx-auto mt-12 md:mt-16 rounded-3xl border border-primary-100 bg-white p-6 md:p-8 shadow-sm text-center">
          <p className="font-default text-lg md:text-xl text-primary-600 font-semibold leading-relaxed">
            We want people to succeed because they understand the work, feel
            supported in doing it well, and have room to grow as their skills,
            confidence, and judgment develop.
          </p>

          <p className="font-default text-muted-foreground leading-relaxed mt-4">
            That means direct feedback, practical support, respectful
            communication, and opportunities to take ownership as trust and
            experience grow.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Button variant="outline" size="lg" className="border-primary-200 text-primary-600 hover:bg-primary-50 rounded-md shadow-sm" asChild>
              <Link to="/TeamAndCareersPage">View Opportunities</Link>
            </Button>

            <Button size="lg" className="bg-primary-600 hover:bg-primary-700 text-primary-foreground rounded-md shadow-sm" asChild>
              <Link to="/ContactPage">Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}